import {
  PaginationParams,
  DataElement,
  ReferencePhrase,
  AddInstr,
  Doc,
  PhraseWord,
  CatMatch,
  ProcessRunStatusEnum,
  AnnotationElement,
  GrammarSpellingIssue,
  ReferenceIssue,
  AssociatedTextPhrase,
  AssociatedTextFinding,
  AssociatedImage,
  AssociatedImageFinding,
  ConsistencyIssuesToUpdateDto,
  AnchorResult,
  BoundingBoxAnchorResult,
  CopiedAnchorResult,
} from './common';
import { SelectItem, TreeNode } from 'primeng/api';
import { Env } from './user.model';
import { RevRule } from './rev.model';
import { Subject } from 'rxjs';
import { ConsistencyIssue } from '@shared/components/issues/factory/model/consistency-issue.models';
import { ComplianceIssue } from '@shared/components/issues/factory/model/compliance-issue.models';
import { FairBalanceIssue } from '@shared/components/issues/factory/model/fair-balance-issue.models';
import { IssuesFactory } from '@shared/components/issues/factory/issues.factory';
import { EmbeddedObjectAnnotation } from './embedded-object-annotation';

export class Catalog implements SelectItem {
  catId: number;
  catDescr: string;
  activeVersionId: number;
  productId: number;
  productDescr: string;
  typeCode: string;
  typeDescr: string;
  claimsManagementType?: any;
  catVersionId: number;
  catVersions: Catalog[] = [];
  versionId: number;
  versionName: string;
  createDate: string;
  statusCode: string;
  statusDescr: string;
  statusDate: string;
  totDocCnt: number;
  attachedDocsList: DataElement[] = [];
  catAssetTypeList: DataElement[] = [];
  extSw: boolean;
  extCat: Catalog[] = [];
  refMatchTypeCode: string;
  langCode: string;
  langDescr: string;
  isMaster: boolean;
  systemVersion?: number | null;
  useOldVersion: boolean;

  get hasPublished(): boolean {
    return (
      this.catVersions.find(
        (c) => c.statusCode == CatalogStatusEnum.PUBLISHED
      ) != null
    );
  }

  get label(): string {
    return this.catDescr;
  }

  get value() {
    return this;
  }
}

export class CatalogSearchParams extends PaginationParams {
  envId?: number;
  catIdName?: string;
  catId?: number;
  typeCode?: string;
  products?: DataElement[] = [];
  extSw?: boolean;

  constructor() {
    super();
  }
}

export class CatDoc extends Doc {
  versionId: number;
  fileName: string;
  createDate: string;
  lastVersionId: number;
  inCatVersionId: number;
  statusCode: string;
  statusDescr: string;
  progressPct: number;
  docType: string;
  histDocs: CatDoc[] = [];
  pendingCnt: number;
  approvedCnt: number;
  approvedVariantCnt: number;
  rejectedCnt: number;
  parentDocId: number;
  catId: number;
  catVersionId: number;
  statusDate: string;
  productId: number;
  runStatusCode: string;
  runStatusMessage: string;
  singlePhrasePageUrl: string;
  singlePhraseEl: string;
  singlePhrasePageNum: number;
  boundingBoxes: BoundingBox[] = [];
  attachedCardSw: boolean;
  assetType: DataElement;
  annotation: AnnotationElement;
  bulkSeq: number;
  origDocId: number;
  taggedSw: boolean;
  catDescr: string;
  catStatusCode: string;
  catStatusDescr: string;
  productDescr: string;
  createDocType: string;
  catalog: Catalog;
  taggedDocId: number;
  inddDocS3Key: string;
  inddCardS3Key: string;
  disableCanvasViewer: boolean;
  metadata?: {[key: string]: string[]};
  constructor() {
    super();
    this.docObjectType = 'IND';
  }

  get isProcessRunning(): boolean {
    return (
      this.runStatusCode == ProcessRunStatusEnum.RUNNING ||
      this.runStatusCode == ProcessRunStatusEnum.PENDING ||
      this.runStatusCode == ProcessRunStatusEnum.PENDING_CHECKS
    );
  }
}

export class ReferenceDocument extends CatDoc {
  mappedReferenceTexts: string[] = [];
  anchors: AnchorResult[] = []
}



export class CatEntriesSearchParams extends PaginationParams {
  text?: string;
  docs?: DataElement[];
  subTypeCodes?: string[];
  catEntryStatus?: DataElement;
  refStarterSw?: boolean;
  constructor() {
    super();
  }
}

export class CatImagesSearchParams extends PaginationParams {
  constructor() {
    super();
  }
}

export class AltRef {
  catRefAltId: number;
  txt: string;
  phrases: ReferencePhrase[] = [];
}

export class CatAltRefSearchParams extends PaginationParams {
  constructor() {
    super();
  }
}

export class CardSearchParams {
  shortModeSw: boolean;
  docId: number;
  cardId: number;
  catVersionId: number;
  lcId: number;
  title: string;
  dispatch: boolean = false;
}

export class CatDocsSearchParams extends PaginationParams {
  docIdName?: string;
  status?: DataElement;
  docId?: number;
  includeInactiveSw?: boolean;

  constructor() {
    super();
  }
}

export class CatPhraseType {
  typeCode: string;
  typeDescr: string;
  subTypes: CatPhraseSubType[] = [];
  original: string;

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value  
    })  
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as CatPhraseType;
    Object.keys(this).forEach((k) => {
      if (k !== 'original' && k !== 'subTypes') this[k] = original[k];
    });
  }

  isDirty(): boolean {
    let dirty = false;

    let original = JSON.parse(this.original) as CatPhraseType;
    Object.keys(this).forEach((k) => {
      if (k !== 'original' && k !== 'subTypes' && this[k] !== original[k])
        dirty = true;
    });

    return dirty;
  }
}

export class CatPhraseSubType {
  typeCode: string;
  typeDescr: string;
  subTypeCode: string;
  subTypeDescr: string;
  errorLevel: number;
  level0: number;
  level1: number;
  algKeyWords: string;
  original: string;

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value;
    })
  }
  

  restoreOriginal() {
    let original = JSON.parse(this.original) as CatPhraseSubType;
    Object.keys(this).forEach((k) => {
      if (k !== 'original') this[k] = original[k];
    });
  }

  isDirty(): boolean {
    let dirty = false;

    let original = JSON.parse(this.original) as CatPhraseSubType;
    Object.keys(this).forEach((k) => {
      if (k !== 'original' && this[k] !== original[k]) dirty = true;
    });

    return dirty;
  }
}

export class CatPhraseTypeModel {
  types: CatPhraseType[] = [];
  treeTypes: TreeNode[] = [];
  treeTypesForInput: TreeNode[] = [];

  buildTree() {
    this.treeTypes = [];
    this.types.forEach((t) => {
      let node = {
        label: t.typeDescr,
        key: t.typeCode,
        value: t,
        data: {
          typeCode: t.typeCode,
          typeDescr: t.typeDescr,
          treeLevel: 0,
        },
      };

      if (t.subTypes.length > 0) {
        let children = [];
        t.subTypes.forEach((s) => {
          children.push({
            label: s.subTypeDescr,
            key: s.subTypeCode,
            value: s,
            data: {
              typeCode: s.subTypeCode,
              typeDescr: s.subTypeDescr,
              errorLevel: s.errorLevel,
              level0: s.level0,
              level1: s.level1,
              algKeyWords: s.algKeyWords,
              treeLevel: 1,
            },
          });
        });
        node['children'] = children;
      }

      this.treeTypes.push(node);
    });

    this.treeTypesForInput = JSON.parse(JSON.stringify(this.treeTypes));
    this.treeTypesForInput.forEach((n) => (n.selectable = false));
  }

  getSubTypeNode(
    tree: TreeNode[],
    typeCode: string,
    subTypeCode: string
  ): TreeNode {
    let parent = tree.find((n) => n.key == typeCode);
    if (parent) return parent.children.find((n) => n.key == subTypeCode);
    else return null;
  }

  getTreeBySelectedType(typeCode: string, subTypeCode: string): TreeNode[] {
    let tree = this.treeTypesForInput;
    tree.forEach((n) => {
      n.selectable = false;
      n.expanded = false;
      if (n.key == typeCode) {
        n.expanded = true;
      }
    });

    return tree;
  }
}

export class IndElement extends AnnotationElement {
  elementId: number;
  pageNum: number;
  statusCode: string;
  statusDescr: string;
  userStatus: DataElement;
  errorLevel: number;
  similarityCode: string;
  similarityDescr: string;
  similarityPct: number;
  addInstr: AddInstr[] = [];
  checkedSw: boolean;
  viewSw: boolean;
  catMatches: CatMatch[] = [];
  docs: CatDoc[] = [];
  catId: number;
  docId: number;
  sortOrder: number;
  typeCode: string;
  typeDescr: string;
  subTypeCode: string;
  subTypeDescr: string;
  manualSrcSw: boolean;
  original: string;
  catEntryStatus: DataElement;
  extRefs: ExtRef[] = [];
  contextCatPhraseId?: number;

  figureType: string;
  figureTypeDescr: string;
  figureSubType: DataElement;
  children: {[type: string]: IndElement[]} = {};
  isParentActionSelected: boolean = false;
  parentPhraseId: number | null;
  numberOfContextCatPhrases: number = 0;
  numberOfReferencedBy: number = 0;
  numberOfAssociatedBy: number = 0;
  numberOfTables: number = 0;
  numberOfImages: number = 0;
  numberOfFootnotes: number = 0;
  consistencyIssues: ConsistencyIssue[] = [];
  fairBalanceIssues: FairBalanceIssue[] = [];
  complianceIssues: ComplianceIssue[] = [];
  consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto;
  consistencyIssuesDirty = false;
  contentChildren: IndElement[] = [];
  imageChildren: IndElement[] = [];
  showAssociatedTextSection: boolean = true;
  showReferencesSection: boolean = true;
  showAssociatedImagesSection: boolean = true;
  showAssociatedBySection: boolean = true;
  showReferencedBySection: boolean = true;
  showFootnotesSection: boolean = false;
  isFullyFetched: boolean = false;
  showTablesSection: boolean = true;
  showAssociatedTablesSection: boolean = true;
  showImagesSection: boolean = true;
  associatedBy: AssociatedTextPhrase[] = [];
  propagateActionFromParent = new Subject();
  disableConsistencyCheckResultsActions = false;
  isDirtyConsistencyCheckResults = false;
  get hasId(): boolean {
    return this.elementId !== undefined;
  }

  saveOriginal() {}

  restoreOriginal() {
    this.consistencyIssuesDirty = false;
  }

  isDirty(): boolean {
    return false;
  }
}

export class IndImage extends IndElement {
  imageId: number;
  parentImageId: number;
  imgUrl: string;
  imgS3Key: string;
  imageSimilarityEvent: string;
  imageSimilarityEventDesc: string;
  imageFindingEvents: string[] = [];
  imageActions: string[] = [];
  figureCategory: string;
  constructor() {
    super();
    this.annIdPrefix = 'img';
  }

  saveOriginal() {
    this.addInstr.forEach((i) => i.saveOriginal());
    //this.original = JSON.stringify(this);
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original" || key == "propagateActionFromParent") {
         return undefined; // Skip this property
      }
    return value;
});
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as IndPhrase;
    this.typeCode = original.typeCode;
    this.typeDescr = original.typeDescr;
    this.subTypeCode = original.subTypeCode;
    this.subTypeDescr = original.subTypeDescr;
    this.figureSubType = original.figureSubType;
    this.consistencyIssuesToUpdate = original.consistencyIssuesToUpdate;
    this.consistencyIssues = original.consistencyIssues;
    this.disableConsistencyCheckResultsActions = original.disableConsistencyCheckResultsActions;
    this.isDirtyConsistencyCheckResults = false;
    
    this.addInstr = original.addInstr.map((r) => {
      let newInstr = new AddInstr();
      Object.assign(newInstr, r);
      return newInstr;
    });
  }

  isDirty(): boolean {
    let original = JSON.parse(this.original) as IndPhrase;
    let addInstrCntDiff = this.addInstr.length !== original.addInstr.length;
    let consistencyIssuesDiff = this.consistencyIssues?.length != original.consistencyIssues?.length;
    let instrDiff;
    this.addInstr.forEach((r) => {
      let orig = original.addInstr.find((o) => o.id == r.id);
      if (!orig) {
        instrDiff = true;
      } else if (r.isDirty()) {
        instrDiff = true;
      }
    });
    this.isDirtyConsistencyCheckResults = consistencyIssuesDiff || this.consistencyIssuesDirty;

    let typeDiff =
      this.typeCode != original.typeCode ||
      this.subTypeCode != original.subTypeCode ||
      this.figureSubType?.id != original.figureSubType?.id;
    return addInstrCntDiff || instrDiff || typeDiff || consistencyIssuesDiff;
  }
}

export class IndPhrase extends IndElement {
  phraseId: number;
  phraseText: string;
  isRefSw: boolean;
  references: ReferencePhrase[] = [];
  referencesInFootnotesSection: ReferencePhrase[] = [];
  associatedText: AssociatedTextPhrase[] = [];
  associatedImages: AssociatedImage[] = [];
  deletedReferences: ReferencePhrase[] = [];
  deletedAssociatedText: AssociatedTextPhrase[] = [];
  referencedBy: ReferencePhrase[] = [];
  phraseWords: PhraseWord[] = [];
  grammarSpellingIssues: GrammarSpellingIssue[];
  referenceIssues: ReferenceIssue[];
  assocTextFindings: AssociatedTextFinding[];
  assocImageFindings: AssociatedImageFinding[];
  mergeOptions: MergeLine[] = [];
  inCardCheckedSw: boolean;
  lastBBId: number;
  docId: number;
  transTxt: string;
  dirtySw: boolean;
  phraseOrder: number;
  phraseContentType: string;
  phraseContentTypeDesc: string;
  phraseSimilarityEvent: string;
  phraseSimilarityEventDesc: string;
  phraseFindingEvents: string[] = [];
  phraseActions: string[] = [];
  anchorsPathsToUpdate: {
    anchors: {
      ref_doc_id: number;
      ref_doc_anchor_id: number;
      data: {
        path: string
      }
    }[]
  } | null = null;
  boundingBoxAnchorsToAdd: BoundingBoxAnchorResult[] = []; 
  copiedAnchors: CopiedAnchorResult[] = [];
  phraseFindingCategoryDescs: [string, string][] = []
  complianceIssues: ComplianceIssue[] = [];
  saveOriginal() {
    this.addInstr.forEach((i) => i.saveOriginal());
    this.references.forEach((i) => i.saveOriginal());
    this.associatedText.forEach((i) => i.saveOriginal());
    this.catMatches?.forEach((i) => i.saveOriginal());
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original" || key == 'propagateActionFromParent') {
         return undefined; // Skip this property
      }
      return value;  
    })  
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as IndPhrase;
    this.typeCode = original.typeCode;
    this.typeDescr = original.typeDescr;
    this.subTypeCode = original.subTypeCode;
    this.subTypeDescr = original.subTypeDescr;
    this.catEntryStatus = original.catEntryStatus;
    this.consistencyIssuesToUpdate = original.consistencyIssuesToUpdate;
    this.consistencyIssues = original.consistencyIssues;
    this.fairBalanceIssues = original.fairBalanceIssues;
    this.complianceIssues = original.complianceIssues;
    this.associatedImages = original.associatedImages;
    this.referenceIssues = original.referenceIssues;
    this.boundingBoxAnchorsToAdd = [];
    this.copiedAnchors = [];
    if (original.references != null && original.references.length > 0) {
      this.references = original.references.map((r) => {
        let newRef = new ReferencePhrase();
        Object.assign(newRef, r);
        if (r.anchors && r.anchors.length > 0) {

          newRef.anchors = r.anchors.map((a) => {

            let restoreAnchorResult = new AnchorResult(a.issueId, a.refDocAnchorId, a.refDocId, a.embeddedObjectAnnotation);
            return restoreAnchorResult;
            
          });
        }
        newRef.saveOriginal();
        return newRef;
      });
    } else {
      this.references = [];
    }

    this.isDirtyConsistencyCheckResults = false;
    this.disableConsistencyCheckResultsActions = false;
    this.associatedText = original.associatedText.map((r) => {
      let newAssocText = new AssociatedTextPhrase();
      Object.assign(newAssocText, r);
      return newAssocText;
    });
    this.addInstr = original.addInstr.map((r) => {
      let newInstr = new AddInstr();
      Object.assign(newInstr, r);
      return newInstr;
    });
    this.catMatches?.forEach((i) => i.restore());
    this.anchorsPathsToUpdate = original.anchorsPathsToUpdate;
  }

  isDirty(): boolean {
    let original = JSON.parse(this.original) as IndPhrase;
    let phraseDiff = this.phraseText != original.phraseText;
    let statusDiff = this.catEntryStatus?.id != original.catEntryStatus?.id;
    let consistencyIssuesDiff = this.consistencyIssues?.length != original.consistencyIssues?.length;
    let fairBalanceIssuesDiff = this.fairBalanceIssues?.length != original.fairBalanceIssues?.length;
    let complianceIssuesDiff = this.complianceIssues?.length != original.complianceIssues?.length;
    let boundingBoxesAnchorsToAddDiff = this.boundingBoxAnchorsToAdd?.length > 0;
    //let copiedAnchorsDiff = this.copiedAnchors?.length > 0;
    let typeDiff =
      this.typeCode != original.typeCode ||
      this.subTypeCode != original.subTypeCode;

    let refCntDiff = this.references.length !== original.references.length;
    let refDiff;
    this.references.forEach((r) => {
      let origRef = original.references.find(
        (o) => o.refPhraseId == r.refPhraseId
      );
      if (!origRef) {
        refDiff = true;
      } else if (r.isDirty()) {
        refDiff = true;
      }
    });
    let isDirtyCatMatch = false;
    for (let i=0; i<this.catMatches?.length; i++) {
        if (this.catMatches[i].isDirty()) {
          isDirtyCatMatch = true;
          break;
        }
    }
    
    let assocTextCntDiff = this.associatedText.length !== original.associatedText.length;
    let assocTextDiff;
    this.associatedText.forEach((r) => {
      let origAssocText = original.associatedText.find(
        (o) => o.assocPhraseId == r.assocPhraseId
      );
      if (!origAssocText) {
        assocTextDiff = true;
      }
    });

    let addInstrCntDiff = this.addInstr.length !== original.addInstr.length;
    let instrDiff;
    this.addInstr.forEach((r) => {
      let orig = original.addInstr.find((o) => o.id == r.id);
      if (!orig) {
        instrDiff = true;
      } else if (r.isDirty()) {
        instrDiff = true;
      }
    });
    this.isDirtyConsistencyCheckResults = consistencyIssuesDiff || (this.anchorsPathsToUpdate != null) || refDiff || fairBalanceIssuesDiff || complianceIssuesDiff || boundingBoxesAnchorsToAddDiff || this.consistencyIssuesDirty;
    return (
      phraseDiff ||
      typeDiff ||
      refCntDiff ||
      refDiff ||
      assocTextCntDiff ||
      assocTextDiff ||
      addInstrCntDiff ||
      instrDiff ||
      statusDiff ||
      consistencyIssuesDiff ||
      isDirtyCatMatch ||
      fairBalanceIssuesDiff ||
      complianceIssuesDiff ||
      this.consistencyIssuesDirty ||
      this.anchorsPathsToUpdate != null ||
      boundingBoxesAnchorsToAddDiff
    );
  }
}

export class IndPhraseSearchParams {
  phrase: string;
  matchStatus: DataElement;
  phraseStatus: DataElement;
  contentType: DataElement;
  matchType: DataElement;
  subTypeList: string[] = [];
  currLoadedIndPhraseSearchResultNum?: number;

}

export class MergeLine {
  id: number;
  text: string;
  inUseSw: boolean;
  phraseId: number;
  phraseWords: PhraseWord[] = [];
  displayWordsSw: boolean;
  uniqueId: string;

  get label(): string {
    return this.text;
  }

  get value() {
    return this;
  }
}

export class Card {
  cardId: number;
  type: DataElement;
  title: string;
  createDate: string;
  cardPhrases: IndPhrase[] = [];
  cardDocs: CatDoc[] = [];
  mcType: DataElement;
  mcSubType: DataElement;
  lcCardType: DataElement;
  lcId: number;
  matchedCardId: number;
  matchStatusCode: string;
  matchStatusDescr: string;
  matchedCard: Card;
  srcType: string;
  transLang: DataElement[] = [];
  sortOrder: number;
  cardStatus: string;
  original: string;

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value; 
    })   
     this.cardPhrases.forEach((i) => i.saveOriginal());
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as Card;
    Object.keys(this).forEach((k) => {
      if (k !== 'original' && k !== 'cardPhrases') this[k] = original[k];
    });

    this.cardPhrases = original.cardPhrases.map((r) => {
      let newRef = new IndPhrase();
      Object.assign(newRef, r);
      return newRef;
    });
  }

  isDirty(): boolean {
    if (this.original == null) return false;
    let original = JSON.parse(this.original) as Card;
    let titleDiff = this.title != original.title;
    let typeDiff = this.type.id != original.type.id;
    let mcTypeDiff = this.mcType?.id != original.mcType?.id;
    let mcSubTypeDiff = this.mcSubType?.id != original.mcSubType?.id;
    let phraseCntDiff = this.cardPhrases.length !== original.cardPhrases.length;
    let phraseDiff;

    this.cardPhrases.forEach((r) => {
      let origRef = original.cardPhrases.find(
        (o) => o.phraseId == r.phraseId && o.sortOrder == r.sortOrder
      );
      if (!origRef) {
        phraseDiff = true;
      }
    });

    original.cardPhrases.forEach((r) => {
      let origRef = this.cardPhrases.find(
        (o) => o.phraseId == r.phraseId && o.sortOrder == r.sortOrder
      );
      if (!origRef) {
        phraseDiff = true;
      }
    });

    return (
      titleDiff ||
      typeDiff ||
      phraseCntDiff ||
      phraseDiff ||
      mcTypeDiff ||
      mcSubTypeDiff
    );
  }
}

export class BoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  elementId: string;
  boxId: number;
  boxType: DataElement;
  card: Card;
  descr: string;
  pageNum: number;
  displayPageNum: number;
  pageUrl: string;
  imgUrl: string;
  s3Key: string;
  indPhrases: IndPhrase[];
  revPhrases: RevRule[];
  pageWidth: number;
  pageHeight: number;
  deleting: boolean;
  elements: HTMLElement[] = [];
  mcBoxType: DataElement;
  blockRules: string;
  useForPrecheckSw: boolean;
  dirtySw: boolean;
  cardImages: CardImage[];
}

export class CardImage {
  s3Key: string;
  figureType: string;
  catVersionId: number;
  catCardRefImageId: number;
  catCardPhraseId: number;
  imgUrl: string;
}

export class CatDocFileWrapper {
  file: File;
  docName: string;
  parentDoc: CatDoc;
  uploading: boolean;
  docS3Key: string;
  docFileName: string;
  doc: CatDoc;
  finished: boolean;
}

export class McSubType {
  subType: DataElement;
  mcTypeCode: string;
}

export class McBoxType {
  mcTypeCode: string;
  mcSubTypeCode: string;
  mcBoxType: DataElement;
  blockRules: string;
}

export class CardAssetTemplate {
  id: number;
  name: string;
  descr: string;
  assetType: DataElement;
  url: string;
  previewUrl: string;
  cards: Card[] = [];
}

export class ExtRef {
  extRefId: number;
  srcDocId: number;
  docName: string;
  srcPhraseId: number;
  phraseDispWords: PhraseWord[] = [];
  pageNum: number;
  type: string;
  extPageNum: number;
  extDocId: number;
  extType: string;
  extPhraseId: number;
  extPhraseDispWords: PhraseWord[] = [];
  extDocName: string;
  extCatDescr: string;
  extCatVersionId: number;
  ann: AnnotationElement;
  extAnn: AnnotationElement;
}

export class CatExtRefSearchParams {
  docId?: number;
  extDocId?: number;
  phraseText?: string;
  extPhraseText?: string;

  constructor() {}
}

export class CatDocState {
  indDocPhrases?: IndPhrase[] = [];
  indDocImages?: IndImage[] = [];
  curCatDocPageNum?: number = 1;
  catPhrasesSearchParams?: IndPhraseSearchParams = null;
  catDocBoundingBoxes?: BoundingBox[] = [];
  catDocRefOptions?: IndPhrase[] = [];
  catDocPageLines?: MergeLine[] = [];
  catDocCards?: Card[] = [];
  curCatDoc?: CatDoc = null;
}

export class CatDocStateTagEnum {
  static PRIME: string = 'PRIME';
  static SEC: string = 'SEC';
}

export class DirectLinkCatalogDoc {
  docId: number;
  catId: number;
  catVersionId: number;
  page: number;
  dlDocHandled: boolean;
}

/*** ENUMS */

export class CatalogStatusEnum {
  public static DRAFT: string = 'DRAFT';
  public static PUBLISHED: string = 'PUBLISHED';
  public static HIST: string = 'HIST';
  public static CLOSED: string = 'CLOSED';
}

export class CatDocStatusEnum {
  public static DRAFT: string = 'DRAFT';
  public static DETACHED: string = 'DETACHED';
  public static HIST: string = 'HIST';
  public static ADDED_TO_CATALOG: string = 'ADDED_TO_CATALOG';
  public static INACTIVE: string = 'INACTIVE';
}

export class CatPhraseStatusEnum {
  public static PENDING: string = 'PENDING';
  public static APPROVE: string = 'APPROVE';
  public static APPROVE_VAR: string = 'APPROVE_VARIANT';
  public static REJECT: string = 'REJECT';
}
